import React, { useEffect, useState } from 'react';
import { Activity, Loader2, Search, LogIn, Edit, Trash2, Plus, Key, ShieldAlert } from 'lucide-react';
import { adminApi } from '../lib/api';

const ACTION_META = {
  'login.success': { icon: LogIn, color: 'text-emerald-600 bg-emerald-100', label: 'Connexion réussie' },
  'login.failed': { icon: ShieldAlert, color: 'text-red-600 bg-red-100', label: 'Échec de connexion' },
  'password.changed': { icon: Key, color: 'text-purple-600 bg-purple-100', label: 'Mot de passe modifié' },
  'reservation.update_status': { icon: Edit, color: 'text-blue-600 bg-blue-100', label: 'Statut réservation' },
  'review.create': { icon: Plus, color: 'text-emerald-600 bg-emerald-100', label: 'Avis créé' },
  'review.update': { icon: Edit, color: 'text-blue-600 bg-blue-100', label: 'Avis modifié' },
  'review.delete': { icon: Trash2, color: 'text-red-600 bg-red-100', label: 'Avis supprimé' },
  'package.create': { icon: Plus, color: 'text-emerald-600 bg-emerald-100', label: 'Formule créée' },
  'package.update': { icon: Edit, color: 'text-blue-600 bg-blue-100', label: 'Formule modifiée' },
  'package.delete': { icon: Trash2, color: 'text-red-600 bg-red-100', label: 'Formule supprimée' },
  'user.create': { icon: Plus, color: 'text-emerald-600 bg-emerald-100', label: 'Utilisateur créé' },
  'user.update': { icon: Edit, color: 'text-blue-600 bg-blue-100', label: 'Utilisateur modifié' },
  'user.delete': { icon: Trash2, color: 'text-red-600 bg-red-100', label: 'Utilisateur supprimé' }
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `il y a ${s}s`;
  if (s < 3600) return `il y a ${Math.floor(s / 60)}min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)}h`;
  return `il y a ${Math.floor(s / 86400)}j`;
};

const ActivityFeed = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAction) params.action = filterAction;
      if (filterUser) params.username = filterUser;
      setItems(await adminApi.getActivity(params));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterAction, filterUser]);

  const filters = [
    { value: '', label: 'Toutes' },
    { value: 'login', label: 'Connexions' },
    { value: 'reservation', label: 'Réservations' },
    { value: 'review', label: 'Avis' },
    { value: 'package', label: 'Formules' },
    { value: 'user', label: 'Utilisateurs' },
    { value: 'password', label: 'Mots de passe' }
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Journal d'activité</h3>
          <p className="text-sm text-slate-500">Historique des actions effectuées dans l'espace admin</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            placeholder="Filtrer par utilisateur..."
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="pl-10 pr-4 py-2 rounded-full border border-slate-200 focus:border-[#F4A340] outline-none text-sm w-full md:w-64"
          />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.value || 'all'}
            onClick={() => setFilterAction(f.value)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filterAction === f.value ? 'bg-[#F4A340] text-white' : 'bg-white border border-slate-200 hover:border-[#F4A340]'
            }`}
          >{f.label}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#F4A340]" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Aucune activité pour ce filtre</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((a) => {
              const meta = ACTION_META[a.action] || { icon: Activity, color: 'text-slate-600 bg-slate-100', label: a.action };
              const Icon = meta.icon;
              return (
                <li key={a.id} className="p-4 hover:bg-slate-50/50 transition-colors flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl ${meta.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-semibold text-slate-900">{a.username}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{a.role}</span>
                      <span className="text-sm text-slate-600">· {meta.label}</span>
                    </div>
                    {a.details && Object.keys(a.details).length > 0 && (
                      <div className="mt-1 text-xs text-slate-500">
                        {Object.entries(a.details).map(([k, v]) => (
                          <span key={k} className="mr-3">{k}: <span className="font-mono">{String(v)}</span></span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-slate-500 whitespace-nowrap">{timeAgo(a.created_at)}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleString('fr-FR')}</div>
                    {a.ip && <div className="text-[10px] text-slate-400 mt-0.5 font-mono">{a.ip}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;
